import type { WorkCalendarData } from "./calendar";
import {
  isWorkedDay,
  standardWorkingDaysInPeriod,
  workedDayKeysInPeriod,
  workedDaysInPeriod,
} from "./calendar";
import type { OrgCalendarConfig, Period, PeriodType } from "./periods";
import { dayKeysInPeriod, periodFromKey, periodKeyFor, periodsInRange } from "./periods";

/**
 * Rollup engine (§5): computed views over observations. Pure — no DB access.
 *
 * - cumulative → SUM of observation values (multiple events per period sum naturally)
 * - average    → daily-cadence metrics: SUM / normalization denominator (worked days /
 *                standard working days / calendar days — the sheet's AVG calc modes);
 *                coarser cadences: mean over sub-periods that have data
 * - yes_no / done_not_done → % true over expected occurrences
 * - missing data is null, never zero; completeness = present/expected
 */

export type Unit =
  | "number"
  | "currency"
  | "percent"
  | "yes_no"
  | "done_not_done"
  | "time"
  | "rating";
export type Aggregation = "cumulative" | "average";
export type NormalizationMode = "worked_days" | "working_days_in_month" | "calendar_days";
export type EntryCadence = "daily" | "weekly" | "monthly" | "quarterly" | "yearly";

export interface ObservationData {
  value: number;
  /** UTC instant */
  occurredAt: Date;
  /** set for period-native entries */
  periodHint?: PeriodType | null;
  /** required when periodHint is set */
  periodKey?: string | null;
}

export interface MetricRollupConfig {
  unit: Unit;
  aggregation: Aggregation;
  normalization: NormalizationMode;
  entryCadence: EntryCadence;
  /** worked_days averages: 'excuse' (default) uses only days with data (+ day-editor
   * counted gaps); 'count' is strict sheet-style — every worked day divides. */
  gapPolicy?: "excuse" | "count";
}

export interface RollupResult {
  value: number | null;
  expected: number;
  present: number;
  /** present/expected; 1 when nothing was expected */
  completeness: number;
}

export const CADENCE_TO_PERIOD: Record<EntryCadence, PeriodType> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
  quarterly: "quarter",
  yearly: "year",
};

const LEVEL: Record<PeriodType, number> = {
  day: 0,
  week: 1,
  month: 2,
  quarter: 3,
  year: 4,
};

/** The periodKey (at `type` level) an observation belongs to, honoring periodHint.
 * Returns null when the observation lives at a coarser level than `type`
 * (a weekly self-report has no daily location). */
export function observationKeyAt(
  config: OrgCalendarConfig,
  obs: ObservationData,
  type: PeriodType,
): string | null {
  if (obs.periodHint && obs.periodKey) {
    if (obs.periodHint === type) return obs.periodKey;
    if (LEVEL[obs.periodHint] > LEVEL[type]) return null; // coarser than requested
    // finer hint than requested: locate via the hinted period's start instant.
    const hinted = periodFromKey(config, obs.periodHint, obs.periodKey);
    // week→month/quarter/year: a week belongs where its START falls, except
    // week→year which follows the week-year label (weeks roll to the year directly).
    if (obs.periodHint === "week" && type === "year") {
      return obs.periodKey.slice(0, 4);
    }
    return periodKeyFor(config, type, hinted.startsAt);
  }
  return periodKeyFor(config, type, obs.occurredAt);
}

function inPeriod(config: OrgCalendarConfig, obs: ObservationData, period: Period): boolean {
  return observationKeyAt(config, obs, period.periodType) === period.periodKey;
}

/** Sub-periods of `period` at the metric's entry cadence (for expected/present math). */
export function cadenceSubPeriods(
  config: OrgCalendarConfig,
  period: Period,
  cadence: EntryCadence,
): Period[] {
  const t = CADENCE_TO_PERIOD[cadence];
  if (LEVEL[t] > LEVEL[period.periodType]) return []; // cadence coarser than view
  if (t === period.periodType) return [period];
  if (t === "week") {
    // weeks belong to a parent by their start (year: by week-year label)
    return periodsInRange(config, "week", period.startsAt, period.endsAt).filter((w) => {
      if (period.periodType === "year") return w.periodKey.slice(0, 4) === period.periodKey;
      const startKey = periodKeyFor(config, period.periodType, w.startsAt);
      return startKey === period.periodKey;
    });
  }
  return periodsInRange(config, t, period.startsAt, period.endsAt).filter(
    (c) => c.startsAt >= period.startsAt && c.startsAt < period.endsAt,
  );
}

/** Expected number of entries for a metric in a period (drives completeness + yes/no). */
export function expectedOccurrences(
  config: OrgCalendarConfig,
  cal: WorkCalendarData,
  period: Period,
  cadence: EntryCadence,
): number {
  if (cadence === "daily") return workedDaysInPeriod(cal, config, period);
  const subs = cadenceSubPeriods(config, period, cadence);
  // a coarser sub-period is expected only if it contains ≥1 worked day
  return subs.filter((s) => workedDaysInPeriod(cal, config, s) > 0).length;
}

export function rollup(
  metric: MetricRollupConfig,
  config: OrgCalendarConfig,
  cal: WorkCalendarData,
  period: Period,
  observations: ObservationData[],
): RollupResult {
  const inRange = observations.filter((o) => inPeriod(config, o, period));
  const cadenceType = CADENCE_TO_PERIOD[metric.entryCadence];

  if (LEVEL[cadenceType] > LEVEL[period.periodType]) {
    // viewing finer than the metric exists (daily view of a weekly metric)
    return { value: null, expected: 0, present: 0, completeness: 1 };
  }

  const expected = expectedOccurrences(config, cal, period, metric.entryCadence);

  // distinct sub-periods that actually have data
  const presentKeys = new Set<string>();
  for (const o of inRange) {
    const k = observationKeyAt(config, o, cadenceType);
    if (k !== null) presentKeys.add(k);
  }
  const present = presentKeys.size;
  const completeness = expected === 0 ? 1 : Math.min(1, present / expected);

  if (inRange.length === 0) {
    return { value: null, expected, present: 0, completeness: expected === 0 ? 1 : 0 };
  }

  if (metric.unit === "yes_no" || metric.unit === "done_not_done") {
    if (expected === 0) return { value: null, expected, present, completeness };
    const trues = inRange.filter((o) => o.value > 0).length;
    return {
      value: (trues / expected) * 100,
      expected,
      present,
      completeness,
    };
  }

  const sum = inRange.reduce((s, o) => s + o.value, 0);

  if (metric.aggregation === "cumulative") {
    return { value: sum, expected, present, completeness };
  }

  // average
  if (metric.entryCadence === "daily") {
    let denom: number;
    switch (metric.normalization) {
      case "worked_days": {
        if ((metric.gapPolicy ?? "excuse") === "count") {
          denom = workedDaysInPeriod(cal, config, period);
        } else {
          // excused gaps: divide by worked days WITH data, plus any day the
          // day-editor explicitly marked as counting
          const workedKeys = new Set(workedDayKeysInPeriod(cal, config, period));
          const counted = new Set<string>();
          for (const o of inRange) {
            const k = observationKeyAt(config, o, "day");
            if (k && workedKeys.has(k)) counted.add(k);
          }
          for (const [d, v] of Object.entries(cal.gapCounts ?? {})) {
            if (v && workedKeys.has(d)) counted.add(d);
          }
          denom = counted.size;
        }
        break;
      }
      case "working_days_in_month":
        denom = standardWorkingDaysInPeriod(config, period);
        break;
      case "calendar_days":
        denom = dayKeysInPeriod(config, period).length;
        break;
    }
    if (denom === 0) return { value: null, expected, present, completeness };
    return { value: sum / denom, expected, present, completeness };
  }

  // coarser cadences: mean over sub-periods with data (missing is null, never zero)
  const perSub = new Map<string, number>();
  for (const o of inRange) {
    const k = observationKeyAt(config, o, cadenceType);
    if (k === null) continue;
    perSub.set(k, (perSub.get(k) ?? 0) + o.value);
  }
  if (perSub.size === 0) return { value: null, expected, present, completeness };
  const mean = [...perSub.values()].reduce((s, v) => s + v, 0) / perSub.size;
  return { value: mean, expected, present, completeness };
}

/** Worked-day check re-export used by the discipline engine. */
export { isWorkedDay };
